export enum WidgetWriteKind {
  AddComponent = 'add-component',
  EditComponent = 'edit-component',
  DeleteComponent = 'delete-component',
  MoveComponent = 'move-component',
  AddContainer = 'add-container',
  EditContainer = 'edit-container',
  DeleteContainer = 'delete-container',
  MoveContainer = 'move-container',
  AddStep = 'add-step',
  DuplicateStep = 'duplicate-step',
  EditStep = 'edit-step',
  DeleteStep = 'delete-step',
  MoveStep = 'move-step',
  EditLayout = 'edit-layout',
  EditConditions = 'edit-conditions',
  EditSettings = 'edit-settings',
  // Saved on the server by the assistant, so no write below has it.
  AssistantChange = 'assistant-change',
}

export interface WidgetWrite {
  readonly widgetId: number;
  readonly kind: WidgetWriteKind;
}

export type WidgetWriteEvent =
  | { readonly write: WidgetWrite; readonly finished: false }
  | { readonly write: WidgetWrite; readonly finished: true; readonly succeeded: boolean };

interface WriteRule {
  readonly pattern: RegExp;
  readonly method: string;
  readonly kind: WidgetWriteKind;
}

// Paths are relative to the gateway, and the first group is always the variant id.
const RULES: WriteRule[] = [
  { pattern: /^widgets\/conditions\/(\d+)$/, method: 'POST', kind: WidgetWriteKind.EditConditions },
  {
    pattern: /^widgets\/(\d+)\/states\/\d+\/containers\/\d+\/components$/,
    method: 'POST',
    kind: WidgetWriteKind.AddComponent,
  },
  { pattern: /^widgets\/(\d+)\/components(\/\d+)?$/, method: 'PUT', kind: WidgetWriteKind.EditComponent },
  { pattern: /^widgets\/(\d+)\/components(\/\d+)?$/, method: 'PATCH', kind: WidgetWriteKind.EditComponent },
  {
    pattern: /^widgets\/(\d+)\/containers\/\d+\/components\/\d+$/,
    method: 'DELETE',
    kind: WidgetWriteKind.DeleteComponent,
  },
  {
    pattern: /^widgets\/(\d+)\/containers\/\d+\/components\/\d+\/move$/,
    method: 'PUT',
    kind: WidgetWriteKind.MoveComponent,
  },
  { pattern: /^widgets\/(\d+)\/states\/\d+\/containers$/, method: 'POST', kind: WidgetWriteKind.AddContainer },
  { pattern: /^widgets\/(\d+)\/states\/\d+\/containers\/\d+$/, method: 'PUT', kind: WidgetWriteKind.EditContainer },
  {
    pattern: /^widgets\/(\d+)\/states\/\d+\/containers\/\d+$/,
    method: 'DELETE',
    kind: WidgetWriteKind.DeleteContainer,
  },
  {
    pattern: /^widgets\/(\d+)\/states\/\d+\/containers\/\d+\/move$/,
    method: 'PUT',
    kind: WidgetWriteKind.MoveContainer,
  },
  { pattern: /^widgets\/(\d+)\/states$/, method: 'POST', kind: WidgetWriteKind.AddStep },
  { pattern: /^widgets\/(\d+)\/states$/, method: 'PATCH', kind: WidgetWriteKind.EditLayout },
  { pattern: /^widgets\/(\d+)\/states\/\d+$/, method: 'PUT', kind: WidgetWriteKind.EditStep },
  { pattern: /^widgets\/(\d+)\/states\/\d+$/, method: 'DELETE', kind: WidgetWriteKind.DeleteStep },
  { pattern: /^widgets\/(\d+)\/states\/\d+\/move$/, method: 'PUT', kind: WidgetWriteKind.MoveStep },
  { pattern: /^widgets\/(\d+)\/states\/\d+\/duplicate$/, method: 'POST', kind: WidgetWriteKind.DuplicateStep },
  { pattern: /^widgets\/(\d+)\/states\/\d+\/layout$/, method: 'PUT', kind: WidgetWriteKind.EditLayout },
  { pattern: /^widgets\/(\d+)\/layout$/, method: 'PUT', kind: WidgetWriteKind.EditLayout },
  { pattern: /^widgets\/(\d+)\/response-list$/, method: 'PUT', kind: WidgetWriteKind.EditSettings },
  { pattern: /^widgets\/(\d+)$/, method: 'PUT', kind: WidgetWriteKind.EditSettings },
  { pattern: /^widgets\/(\d+)$/, method: 'PATCH', kind: WidgetWriteKind.EditSettings },
];

export function toWidgetWrite(method: string, url: string, gatewayUrl: string): WidgetWrite | null {
  const prefix = `${gatewayUrl}/`;
  if (!url.startsWith(prefix)) {
    return null;
  }

  const path = url.slice(prefix.length).split('?')[0];
  const verb = method.toUpperCase();
  for (const rule of RULES) {
    const match = rule.method === verb ? rule.pattern.exec(path) : null;
    if (match) {
      return { widgetId: Number(match[1]), kind: rule.kind };
    }
  }
  return null;
}
